import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { CaretDownIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { type Control, Controller } from "react-hook-form";

import { InputField } from "~/v2/components/common/InputField";
import { CountryCombobox } from "~/v2/components/form";
import type { Country } from "~/v2/types";
import type { TravelRuleFormValues } from "../schema";
import type {
  RequestedField,
  TravelRuleRequest,
} from "../travelRuleExtraFields";
import { YesNoToggle } from "./YesNoToggle";

const FIELD_LABEL: Record<string, string> = {
  beneficiary_given_name: "First Name",
  beneficiary_family_name: "Last Name",
  beneficiary_entity_name: "Legal Organization Name",
  beneficiary_geographic_address_country: "Country",
  beneficiary_geographic_address_building_number: "Building/Street No.",
  beneficiary_geographic_address_street_name: "Street Name",
  beneficiary_geographic_address_town_name: "Town/City",
  beneficiary_geographic_address_post_code: "Postal Code",
  beneficiary_country_of_residence: "Country of Residence (2-letter code)",
  originator_country_of_residence:
    "Sender Country of Residence (2-letter code)",
  originator_date_of_birth: "Sender Date of Birth (YYYY-MM-DD)",
  originator_place_of_birth: "Sender Place of Birth (2-letter country code)",
  beneficiary_vasp_id: "VASP ID",
  "beneficiary_manual_entry.vasp_name": "VASP Name",
  "beneficiary_manual_entry.vasp_website": "VASP Website",
  beneficiary_is_self_hosted: "Is Self-Hosted",
};

const labelFor = (field: RequestedField): string =>
  FIELD_LABEL[field.path] ?? field.description ?? field.path;

// cryptowallet's `enrichValidationError` appends this verbatim sentence whenever
// any `next_actions[].type === "ONE_OF"` is present, leaking the wire identifier
// `one_of`; stripped so the banner stays free of wire jargon (the radio header
// already tells the user to pick one when present).
const ONE_OF_REMINDER_CLAUSE =
  "Make sure to include at least one of the `one_of` type fields.";

export function formatErrorMessage(message: string): string {
  let formatted = message
    .replace(ONE_OF_REMINDER_CLAUSE, "")
    .replace(/\s+/g, " ")
    .trim();
  // Sort by descending length so longer keys substitute before any shorter prefix.
  const sorted = Object.entries(FIELD_LABEL).sort(
    (a, b) => b[0].length - a[0].length,
  );
  for (const [snakeCase, label] of sorted) {
    formatted = formatted.replaceAll(snakeCase, `"${label}"`);
  }
  return formatted;
}

// Listbox-based select that wraps long option text. Used for enum fields
// (`options.length > 1`).
interface WrappingSelectProps {
  label: string;
  options: { value: string; label: string }[];
  value: string | undefined;
  onChange: (value: string) => void;
  errorMsg?: string;
  helpMsg?: string;
  placeholder?: string;
}

const WrappingSelect = ({
  label,
  options,
  value,
  onChange,
  errorMsg,
  helpMsg,
  placeholder = "Select...",
}: WrappingSelectProps) => {
  const selected = options.find((o) => o.value === value) ?? null;
  return (
    <div className="flex flex-col gap-1">
      <label className="block font-semibold text-gray-700 text-sm">
        {label}
      </label>
      <Listbox value={value ?? ""} onChange={onChange}>
        <div className="relative">
          <ListboxButton className="relative min-h-9 w-full rounded border border-gray-400 bg-white px-2 py-2 pr-8 text-left font-medium text-gray-700 text-sm focus:border-gray-500 focus:outline-none">
            <span className="block whitespace-normal">
              {selected?.label ?? placeholder}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <CaretDownIcon size={14} className="text-gray-500" />
            </span>
          </ListboxButton>
          <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded border border-gray-300 bg-white py-1 shadow-lg focus:outline-none">
            {options.map((o) => (
              <ListboxOption
                key={o.value}
                value={o.value}
                className="cursor-pointer select-none whitespace-normal px-3 py-2 text-gray-800 text-xs data-[focus]:bg-indigo-50 data-[selected]:bg-indigo-100"
              >
                {o.label}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>
      {errorMsg && <p className="m-0 p-0 text-red-500 text-xs">{errorMsg}</p>}
      {!errorMsg && helpMsg && (
        <p className="m-0 p-0 text-gray-500 text-xs">{helpMsg}</p>
      )}
    </div>
  );
};

// Per-field widget priority:
//   1. `*_geographic_address_country` -> Combobox Alpha-3.
//   2. bool / boolean -> YesNoToggle.
//   3. `options.length > 1` enum -> WrappingSelect.
//   4. anything else -> Input.
// Single-option (forced) fields go through AddressMatchModal, not here.
const COUNTRY_FIELD_PATHS: ReadonlySet<string> = new Set([
  "beneficiary_geographic_address_country",
  "originator_geographic_address_country",
]);

const isBoolType = (valueType: string): boolean => {
  const t = valueType.toLowerCase();
  return t === "bool" || t === "boolean";
};

const toBool = (value: unknown): boolean | null => {
  if (typeof value === "boolean") {
    return value;
  }
  if (value === "true") {
    return true;
  }
  if (value === "false") {
    return false;
  }
  return null;
};

interface FieldRendererProps {
  field: RequestedField;
  control: Control<TravelRuleFormValues>;
  countries: Country[];
  /** When true, dim and disable the input (used for non-selected branches). */
  disabled?: boolean;
}

const FieldRenderer = ({
  field,
  control,
  countries,
  disabled,
}: FieldRendererProps) => {
  const label = labelFor(field);
  const wrapperClass = disabled ? "opacity-60 pointer-events-none" : "";

  if (COUNTRY_FIELD_PATHS.has(field.path)) {
    return (
      <div className={wrapperClass}>
        <Controller
          name={field.path}
          control={control}
          render={({ field: rhfField, fieldState: { error } }) => (
            <div className="flex flex-col gap-1">
              <label className="block font-semibold text-gray-700 text-sm">
                {label}
              </label>
              <CountryCombobox
                valueAs="alpha-3"
                name={field.path}
                items={countries}
                value={rhfField.value as string | undefined}
                onChange={rhfField.onChange}
                placeholder="Search country..."
                status={error ? "error" : "neutral"}
                errorMsg={error?.message}
              />
            </div>
          )}
        />
      </div>
    );
  }

  if (isBoolType(field.valueType)) {
    return (
      <div className={wrapperClass}>
        <Controller
          name={field.path}
          control={control}
          render={({ field: rhfField, fieldState: { error } }) => (
            <div className="flex flex-col gap-1">
              <YesNoToggle
                label={label}
                value={toBool(rhfField.value)}
                onChange={rhfField.onChange}
                errorMsg={error?.message}
              />
              {!error?.message && field.description && (
                <p className="m-0 p-0 text-gray-500 text-xs">
                  {field.description}
                </p>
              )}
            </div>
          )}
        />
      </div>
    );
  }

  if (field.options && field.options.length > 1) {
    return (
      <div className={wrapperClass}>
        <Controller
          name={field.path}
          control={control}
          render={({ field: rhfField, fieldState: { error } }) => (
            <WrappingSelect
              label={label}
              options={(field.options ?? []).map((o) => ({
                value: o.value,
                label: o.description || o.value,
              }))}
              value={
                rhfField.value === undefined || rhfField.value === null
                  ? undefined
                  : String(rhfField.value)
              }
              onChange={rhfField.onChange}
              errorMsg={error?.message}
              helpMsg={field.description}
            />
          )}
        />
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <Controller
        name={field.path}
        control={control}
        render={({ field: rhfField, fieldState: { error } }) => (
          <div className="flex flex-col gap-1">
            <InputField
              label={label}
              value={(rhfField.value as string | undefined) ?? ""}
              onChange={(e) => rhfField.onChange(e.target.value)}
              onBlur={rhfField.onBlur}
              error={error?.message}
              fullWidth
            />
            {!error?.message && field.description && (
              <p className="m-0 p-0 text-gray-500 text-xs">
                {field.description}
              </p>
            )}
          </div>
        )}
      />
    </div>
  );
};

// Single render surface for cryptowallet `next_actions` (ADDRESS_BELONGS_TO_VASP
// still routes to AddressMatchModal). REQUIRED fields render flat. ONE_OF
// branches with two-or-more options render under a "Choose one of the following:"
// radio header; only the selected branch contributes to `hasUnfilledRequest` and
// reaches the wire (the controller clears the rest in `onSelectBranch`). A lone
// ONE_OF branch renders flat — no header, no radio — but the controller still
// seeds `selectedBranchId` so submit gating works.
interface RequestedFieldsPanelProps {
  request: TravelRuleRequest;
  control: Control<TravelRuleFormValues>;
  countries: Country[];
  selectedBranchId: string | null;
  onSelectBranch: (branchId: string) => void;
}

interface BranchGroup {
  id: string;
  label?: string;
  fields: RequestedField[];
}

const groupFields = (
  fields: RequestedField[],
): { required: RequestedField[]; branches: BranchGroup[] } => {
  const required: RequestedField[] = [];
  const branchById = new Map<string, BranchGroup>();
  for (const f of fields) {
    if (!f.branchId) {
      required.push(f);
      continue;
    }
    const existing = branchById.get(f.branchId);
    if (existing) {
      existing.fields.push(f);
    } else {
      branchById.set(f.branchId, {
        id: f.branchId,
        label: f.branchLabel,
        fields: [f],
      });
    }
  }
  return { required, branches: Array.from(branchById.values()) };
};

export const RequestedFieldsPanel = ({
  request,
  control,
  countries,
  selectedBranchId,
  onSelectBranch,
}: RequestedFieldsPanelProps) => {
  const { required, branches } = groupFields(request.fields);

  return (
    <div className="flex flex-col gap-3 pt-5">
      <div className="flex items-start gap-2">
        <WarningCircleIcon
          size={18}
          weight="fill"
          className="mt-0.5 shrink-0 text-amber-500"
        />
        <p className="m-0 p-0 font-medium text-amber-800 text-sm">
          {formatErrorMessage(request.banner)}
        </p>
      </div>

      {required.map((f) => (
        <FieldRenderer
          key={f.path}
          field={f}
          control={control}
          countries={countries}
        />
      ))}

      {branches.length === 1 &&
        branches[0].fields.map((f) => (
          <FieldRenderer
            key={f.path}
            field={f}
            control={control}
            countries={countries}
          />
        ))}

      {branches.length >= 2 && (
        <div className="flex flex-col gap-2 border-gray-200 border-t pt-2">
          <p className="m-0 font-semibold text-gray-700 text-sm">
            Choose one of the following:
          </p>
          <div className="flex flex-col gap-3">
            {branches.map((branch) => {
              const isSelected = selectedBranchId === branch.id;
              return (
                <div key={branch.id} className="flex flex-col gap-2">
                  <label className="flex cursor-pointer items-start gap-2">
                    <input
                      type="radio"
                      name="requested-fields-branch"
                      checked={isSelected}
                      onChange={() => onSelectBranch(branch.id)}
                      className="mt-1 h-4 w-4 shrink-0 cursor-pointer"
                    />
                    <span className="text-gray-800 text-sm">
                      {branch.label ?? `Option ${branch.id}`}
                    </span>
                  </label>
                  <div className="flex flex-col gap-3 pl-6">
                    {branch.fields.map((f) => (
                      <FieldRenderer
                        key={f.path}
                        field={f}
                        control={control}
                        countries={countries}
                        disabled={!isSelected}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
